/**
 * WooCommerce Blocks fallbacks
 *
 * The Pixel Manager reads the blocks funnel through the
 * experimental__woocommerce_blocks-* wp.hooks actions (see wc_hooks.js). Those
 * actions are dispatched by the React implementation of the blocks. As blocks
 * move to the Interactivity API they stop dispatching them: the Interactivity
 * API cart store emits no wp.hooks action at all, its only outward signal is a
 * wc-blocks_added_to_cart DOM event whose detail carries no product and no
 * quantity.
 *
 * This file closes both ends of that:
 *
 * - It listens to the DOM events the Interactivity API cart store does emit and
 *   reconciles the data layer cart against the server when a cart change
 *   happened that no tracked interaction accounts for.
 *
 * - It adds DOM level fallbacks for the Cart and Checkout blocks, which are
 *   still React today. They exist so that the day those blocks migrate, the
 *   checkout funnel degrades to a working DOM path instead of going silent.
 *
 * Everything here is deduplicated against the hook path by value and time
 * rather than by asking whether the hooks are still in use. Both paths fire
 * within the same interaction, so whichever arrives first wins and the other
 * is suppressed. That holds no matter which implementation WooCommerce ships.
 *
 * @since 1.65.0
 */

(function (pmw, $, undefined) {

	/**
	 * Last emission timestamp per event key.
	 *
	 * @type {Object.<string, number>}
	 */
	const lastEmitted = {};

	/**
	 * Emit a blocks funnel event unless the same event was already emitted for
	 * the same value a moment ago.
	 *
	 * @param {string}   key      Event key, optionally suffixed by the value it carries.
	 * @param {number}   windowMs How long an emission suppresses the next one.
	 * @param {Function} emit
	 */
	pmw.emitBlocksEventOnce = (key, windowMs, emit) => {

		try {

			const now = Date.now();

			if (lastEmitted[key] && (now - lastEmitted[key]) < windowMs) return;

			lastEmitted[key] = now;

			emit();

		} catch (e) {
			pmw.console.error(e);
		}
	};

	/**
	 * How long a cart change tracked through the normal paths (the add to cart
	 * click handlers, the wp.hooks cart actions) accounts for a cart mutation
	 * signalled by the blocks.
	 *
	 * @type {number}
	 */
	const CART_TRACKED_GRACE_MS = 4000;

	/**
	 * Whether a cart change was tracked recently enough to explain a cart
	 * mutation event coming from the blocks.
	 *
	 * @returns {boolean}
	 */
	const cartChangeWasTracked = () =>
		!!pmw.lastTrackedCartChange && (Date.now() - pmw.lastTrackedCartChange) < CART_TRACKED_GRACE_MS;

	/**
	 * Handle a cart mutation announced by WooCommerce Blocks.
	 *
	 * When one of our own handlers already tracked the change, the data layer
	 * cart is correct and only the stored WooCommerce cart hash needs to catch
	 * up, so the next page load does not mistake our own change for a
	 * server-side desync and re-fetch the cart.
	 *
	 * When nothing tracked it, the change came from a path that emits no
	 * product information (the Interactivity API product button, the Add to
	 * Cart with Options form, the Mini Cart). Reconciling against the server
	 * recovers both the cart contents and the missing events.
	 */
	const handleBlocksCartMutation = () => {

		try {

			if (cartChangeWasTracked()) {

				// WooCommerce updates the cart hash cookie from the Store API
				// response, which can land marginally after the event.
				setTimeout(() => {
					try {
						pmw.storeData("cart_hash", pmw.platform.getCartHash());
					} catch (e) {
						pmw.console.error(e);
					}
				}, 500);

				return;
			}

			pmw.reconcileCartFromBackend();

		} catch (e) {
			pmw.console.error(e);
		}
	};

	/**
	 * Dispatched by the Interactivity API cart store after every successful add
	 * to cart, single and batched. This is the only signal the blockified
	 * product button and the Add to Cart with Options block emit.
	 */
	document.addEventListener("wc-blocks_added_to_cart", handleBlocksCartMutation);

	/**
	 * Dispatched when WooCommerce wants the frontend cart state re-read,
	 * because something changed it outside the blocks' own mutations.
	 */
	document.addEventListener("wc-blocks_store_sync_required", handleBlocksCartMutation);

	/**
	 * Whether a Cart or Checkout block is present on the page.
	 *
	 * The fallbacks below must never run on a classic checkout: the classic
	 * listeners in event_listeners.js already cover it, and their selectors
	 * (input[name='payment_method'], input[name^='shipping_method']) do not
	 * overlap with the block markup, so the two paths stay independent.
	 *
	 * @returns {boolean}
	 */
	const isBlockCheckout = () =>
		!!document.querySelector(".wp-block-woocommerce-checkout, .wc-block-checkout");

	/**
	 * Read a label for a checked radio input, for the shipping tier and payment
	 * method names the events carry.
	 *
	 * @param {Element} input
	 * @returns {{slug: string, text: string}|null}
	 */
	const getBlockRadioOption = input => {

		const $input = jQuery(input);
		const value  = $input.val();

		if (!value) return null;

		let text = "";

		if ($input.attr("id")) {
			text = jQuery("label[for='" + $input.attr("id") + "']").text();
		}

		if (!text) {
			text = $input.closest(".wc-block-components-radio-control__option").text();
		}

		text = String(text).replace(/<[^>]*>?/gm, "").replace(/[\n\t]/gm, "").trim();

		return {
			slug: String(value),
			text: text || String(value),
		};
	};

	/**
	 * add_shipping_info fallback for the Checkout block.
	 *
	 * The block shipping rates carry generated input names, so they can only be
	 * addressed through the shipping method block wrapper. Both the block name
	 * derived wrapper class and the component class are matched, so a themed or
	 * restructured checkout still resolves.
	 */
	jQuery(document).on(
		"change",
		".wp-block-woocommerce-checkout-shipping-method-block input[type='radio'], .wc-block-components-shipping-rates-control input[type='radio']",
		event => {

			if (!isBlockCheckout()) return;

			const option = getBlockRadioOption(event.target);

			if (!option) return;

			pmw.emitBlocksEventOnce("add_shipping_info", 2000, () => {
				pmw.bus.emit("pmw:add-shipping-info", {shippingTier: option});
			});
		}
	);

	/**
	 * add_payment_info fallback for the Checkout block.
	 */
	jQuery(document).on(
		"change",
		".wp-block-woocommerce-checkout-payment-block input[type='radio'], .wc-block-components-checkout-step--payment input[type='radio']",
		event => {

			if (!isBlockCheckout()) return;

			const option = getBlockRadioOption(event.target);

			if (!option) return;

			pmw.emitBlocksEventOnce("add_payment_info", 2000, () => {
				pmw.bus.emit("pmw:add-payment-info", {paymentType: option});
			});
		}
	);

	/**
	 * add_contact_info fallback for the Checkout block.
	 *
	 * Fires when the shopper leaves the email or phone field. The central
	 * pmw:add-contact-info listener drops repeats and incomplete emails, so
	 * this can overlap with the checkout-set-email-address action safely.
	 */
	jQuery(document).on(
		"change",
		".wp-block-woocommerce-checkout-contact-information-block input[type='email'], #email, #billing-phone, #shipping-phone",
		() => {

			if (!isBlockCheckout()) return;

			pmw.bus.emit("pmw:add-contact-info", pmw.platform.getCheckoutContact());
		}
	);

	/**
	 * place_order fallback for the Checkout block.
	 *
	 * A place order click is a one-shot, so a plain time window is enough to
	 * keep it from doubling with the checkout-submit hook.
	 */
	jQuery(document).on("click", ".wc-block-components-checkout-place-order-button", () => {

		if (!isBlockCheckout()) return;

		pmw.emitBlocksEventOnce("place_order", 5000, () => {
			pmw.bus.emit("pmw:place-order", {});
		});
	});

}(window.pmw = window.pmw || {}, jQuery));
